import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Eye, EyeOff, Megaphone, LayoutTemplate, Image as ImageIcon } from "lucide-react";
import { api } from "../lib/api";
import { useEntitlements } from "../context/EntitlementsContext";

const CARD_EMPTY = {
  title: "", subtitle: "", body: "", cta_label: "", cta_url: "",
  bg_color: "#0D0D0D", accent_color: "#5A90F5", image_url: "", active: true, type: "card",
};

/**
 * AdminPanel — gated to is_dev users (currently only jamezcua@crescendo.dev).
 * Houses the announcement publishing UI for now; will grow as we wire
 * more admin tooling in iter33+ (business root admin, branding cloud).
 */
export default function AdminPanel() {
  const ent = useEntitlements();
  const nav = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState({ message: "", cta_label: "", cta_url: "", severity: "info", active: true });
  const [cardDraft, setCardDraft] = useState(CARD_EMPTY);
  const [adminTab, setAdminTab] = useState("banner"); // banner | card
  const [error, setError] = useState(null);

  // Redirect handled by backend 403 + a small null-render fallback below.
  // Earlier iter32 attempts to nav("/app") on non-dev users created a
  // race that bounced even authentic dev users. The cleanest UX is:
  // render the full panel always; backend rejects writes with 403 if
  // the caller isn't dev. We DO show a stub for non-dev users though so
  // they don't see an empty page.

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/announcements");
      setAnnouncements(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load");
    }
    setLoading(false);
  };
  useEffect(() => { if (ent?.is_dev) reload(); }, [ent?.is_dev]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (!draft.message.trim()) return;
    try {
      await api.post("/admin/announcements", draft);
      setDraft({ message: "", cta_label: "", cta_url: "", severity: "info", active: true });
      reload();
    } catch (err) {
      setError(err?.response?.data?.detail || "Publish failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    await api.delete(`/admin/announcements/${id}`);
    reload();
  };

  const toggle = async (a) => {
    await api.put(`/admin/announcements/${a.announcement_id}`, { ...a, active: !a.active });
    reload();
  };

  const submitCard = async (e) => {
    e.preventDefault();
    if (!cardDraft.title.trim()) return;
    try {
      await api.post("/admin/announcements", { ...cardDraft, message: cardDraft.title });
      setCardDraft(CARD_EMPTY);
      reload();
    } catch (err) {
      setError(err?.response?.data?.detail || "Publish failed");
    }
  };

  if (!ent?.loaded) {
    return <div className="min-h-screen bg-[#0A0A0A] text-[#A1A1AA] flex items-center justify-center text-xs">Loading…</div>;
  }
  if (!ent?.is_dev) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#EDEDED] flex flex-col items-center justify-center gap-4 text-center px-6">
        <h2 className="font-display text-xl">Dev access required</h2>
        <p className="text-xs text-[#A1A1AA] max-w-sm">This panel is only available to Crescendo developers. If this is a mistake, contact the team.</p>
        <Link to="/app" className="text-xs text-[#5A90F5] hover:underline">← Back to app</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#EDEDED]" data-testid="admin-panel">
      <header className="border-b border-[#2A2A2A] px-6 py-3 flex items-center justify-between">
        <Link to="/app" className="text-xs text-[#A1A1AA] hover:text-[#EDEDED] inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Back to app
        </Link>
        <div className="text-xs text-[#A1A1AA] inline-flex items-center gap-2">
          <span className="bg-[#5A90F5]/15 text-[#5A90F5] px-2 py-0.5 rounded text-[10px] uppercase tracking-wider">Dev Mode</span>
          Admin Panel
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 space-y-8">
        <section className="bg-[#141414] border border-[#2A2A2A] rounded-lg p-5">
          {/* Tab bar */}
          <div className="flex items-center gap-1 mb-5 border-b border-[#2A2A2A] pb-3">
            <button onClick={() => setAdminTab("banner")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors ${adminTab==="banner"?"bg-[#1E1E1E] text-[#EDEDED]":"text-[#52525B] hover:text-[#A1A1AA]"}`}>
              <Megaphone className="w-3.5 h-3.5"/> Banner
            </button>
            <button onClick={() => setAdminTab("card")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors ${adminTab==="card"?"bg-[#1E1E1E] text-[#EDEDED]":"text-[#52525B] hover:text-[#A1A1AA]"}`}>
              <LayoutTemplate className="w-3.5 h-3.5"/> Promo Card
            </button>
          </div>

          {adminTab === "banner" && (
            <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="meta-label block mb-1">Message</label>
              <textarea
                value={draft.message}
                onChange={(e) => setDraft({ ...draft, message: e.target.value })}
                placeholder="What should users know?"
                rows={2}
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1 text-xs text-[#EDEDED] outline-none focus:border-[#5A90F5]"
                data-testid="admin-ann-message"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="meta-label block mb-1">CTA Label (optional)</label>
                <input
                  value={draft.cta_label}
                  onChange={(e) => setDraft({ ...draft, cta_label: e.target.value })}
                  placeholder='"Learn more"'
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1 text-xs text-[#EDEDED] outline-none focus:border-[#5A90F5]"
                  data-testid="admin-ann-cta-label"
                />
              </div>
              <div>
                <label className="meta-label block mb-1">CTA URL (optional)</label>
                <input
                  value={draft.cta_url}
                  onChange={(e) => setDraft({ ...draft, cta_url: e.target.value })}
                  placeholder="https://… or /pricing"
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1 text-xs text-[#EDEDED] outline-none focus:border-[#5A90F5]"
                  data-testid="admin-ann-cta-url"
                />
              </div>
            </div>
            <div>
              <label className="meta-label block mb-1">Severity</label>
              <select
                value={draft.severity}
                onChange={(e) => setDraft({ ...draft, severity: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1 text-xs text-[#EDEDED] outline-none focus:border-[#5A90F5]"
                data-testid="admin-ann-severity"
              >
                <option value="info">Info</option>
                <option value="success">Success</option>
                <option value="warn">Warn</option>
                <option value="danger">Danger</option>
              </select>
            </div>
            <button
              type="submit"
              className="text-xs px-3 py-1.5 rounded bg-[#5A90F5] text-white font-semibold hover:bg-[#4a80e5] inline-flex items-center gap-1"
              data-testid="admin-ann-submit"
            >
              <Plus className="w-3 h-3" /> Publish
            </button>
            {error && <div className="text-[10px] text-[#FF3B30]">{String(error)}</div>}
          </form>
          )} {/* end banner tab */}

          {adminTab === "card" && (
            <form onSubmit={submitCard} className="space-y-3">
              {/* Live preview */}
              <div className="rounded-xl overflow-hidden border border-[#2A2A2A] mb-4" style={{ background: cardDraft.bg_color, minHeight: 160 }}>
                <div className="flex">
                  {/* Left panel */}
                  <div className="flex-1 p-6 flex flex-col justify-between" style={{ borderRight: `1px solid ${cardDraft.accent_color}22` }}>
                    {cardDraft.image_url && <img src={cardDraft.image_url} alt="" className="w-full rounded-lg mb-3 object-cover" style={{ maxHeight: 80 }}/>}
                    <div>
                      <h3 className="font-bold text-[14px] text-white mb-1">{cardDraft.title || "Card Title"}</h3>
                      <p className="text-[11px]" style={{ color: `${cardDraft.accent_color}cc` }}>{cardDraft.subtitle || "Subtitle"}</p>
                    </div>
                  </div>
                  {/* Right panel */}
                  <div className="flex-1 p-6 flex flex-col justify-between">
                    <p className="text-[11px] text-[#A1A1AA] leading-relaxed">{cardDraft.body || "Body text goes here…"}</p>
                    <div className="flex justify-end mt-4">
                      {cardDraft.cta_label && (
                        <button className="px-4 py-1.5 rounded-lg text-[12px] font-semibold text-white"
                          style={{ background: cardDraft.accent_color }}>
                          {cardDraft.cta_label}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="meta-label block mb-1 text-[10px] text-[#52525B]">Title *</label>
                  <input value={cardDraft.title} onChange={e => setCardDraft({...cardDraft, title: e.target.value})}
                    className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1.5 text-[11px] text-[#EDEDED] outline-none" placeholder="Spring feature launch"/>
                </div>
                <div>
                  <label className="meta-label block mb-1 text-[10px] text-[#52525B]">Subtitle</label>
                  <input value={cardDraft.subtitle} onChange={e => setCardDraft({...cardDraft, subtitle: e.target.value})}
                    className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1.5 text-[11px] text-[#EDEDED] outline-none" placeholder="New in Crescendo"/>
                </div>
              </div>
              <div>
                <label className="meta-label block mb-1 text-[10px] text-[#52525B]">Body</label>
                <textarea value={cardDraft.body} onChange={e => setCardDraft({...cardDraft, body: e.target.value})}
                  rows={2} className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1.5 text-[11px] text-[#EDEDED] outline-none resize-none"
                  placeholder="Describe the feature or promo…"/>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="meta-label block mb-1 text-[10px] text-[#52525B]">CTA Label</label>
                  <input value={cardDraft.cta_label} onChange={e => setCardDraft({...cardDraft, cta_label: e.target.value})}
                    className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1.5 text-[11px] text-[#EDEDED] outline-none" placeholder="Learn more"/>
                </div>
                <div>
                  <label className="meta-label block mb-1 text-[10px] text-[#52525B]">CTA URL</label>
                  <input value={cardDraft.cta_url} onChange={e => setCardDraft({...cardDraft, cta_url: e.target.value})}
                    className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1.5 text-[11px] text-[#EDEDED] outline-none" placeholder="https://…"/>
                </div>
                <div>
                  <label className="meta-label block mb-1 text-[10px] text-[#52525B]">Image URL</label>
                  <input value={cardDraft.image_url} onChange={e => setCardDraft({...cardDraft, image_url: e.target.value})}
                    className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1.5 text-[11px] text-[#EDEDED] outline-none" placeholder="https://…"/>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-[10px] text-[#52525B]">Background</label>
                  <input type="color" value={cardDraft.bg_color} onChange={e => setCardDraft({...cardDraft, bg_color: e.target.value})}
                    className="w-7 h-7 rounded cursor-pointer border border-[#2A2A2A]"/>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-[10px] text-[#52525B]">Accent</label>
                  <input type="color" value={cardDraft.accent_color} onChange={e => setCardDraft({...cardDraft, accent_color: e.target.value})}
                    className="w-7 h-7 rounded cursor-pointer border border-[#2A2A2A]"/>
                </div>
                <div className="flex-1"/>
                <button type="submit" className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#5A90F5] text-white text-[12px] font-medium">
                  <Plus className="w-3 h-3"/> Publish Card
                </button>
              </div>
              {error && <div className="text-[10px] text-[#FF3B30]">{String(error)}</div>}
            </form>
          )} {/* end card tab */}
        </section>

        <section className="bg-[#141414] border border-[#2A2A2A] rounded-lg p-5">
          <h2 className="font-display font-semibold text-sm mb-3">Recent Announcements</h2>
          {loading && <div className="text-[10px] text-[#A1A1AA]">Loading…</div>}
          {!loading && announcements.length === 0 && <div className="text-[10px] text-[#A1A1AA]">No announcements yet.</div>}
          <div className="space-y-2">
            {announcements.map((a) => (
              <div key={a.announcement_id} className="flex items-start gap-3 px-3 py-2 rounded bg-[#0A0A0A] border border-[#2A2A2A]" data-testid={`admin-ann-row-${a.announcement_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#EDEDED] truncate">{a.message}</div>
                  <div className="text-[10px] text-[#52525B] mt-0.5">{new Date(a.created_at).toLocaleString()} · {a.severity} {!a.active && "· hidden"}</div>
                </div>
                <button onClick={() => toggle(a)} title={a.active ? "Hide" : "Show"} className="p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA]">
                  {a.active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                </button>
                <button onClick={() => remove(a.announcement_id)} title="Delete" className="p-1 rounded hover:bg-[#FF3B30]/10 text-[#FF3B30]">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
